/**
 * Section Registry
 *
 * Exports all section components and provides a lookup function.
 */

import { ComponentType } from 'react';
import { SectionType, SectionData, SectionProps } from '../types';

// Import section components
import { Hero } from './Hero';
import { Services } from './Services';
import { About } from './About';
import { Testimonials } from './Testimonials';
import { Gallery } from './Gallery';
import { Cta } from './Cta';
import { Faq } from './Faq';
import { Location } from './Location';
import { Stats } from './Stats';
import { Credentials } from './Credentials';
import { Contact } from './Contact';
import { Process } from './Process';

// =============================================================================
// SECTION REGISTRY
// =============================================================================

type SectionComponent = ComponentType<SectionProps<SectionData>>;

const sectionRegistry: Partial<Record<SectionType, SectionComponent>> = {
  hero: Hero as SectionComponent,
  services: Services as SectionComponent,
  about: About as SectionComponent,
  testimonials: Testimonials as SectionComponent,
  gallery: Gallery as SectionComponent,
  cta: Cta as SectionComponent,
  faq: Faq as SectionComponent,
  location: Location as SectionComponent,
  stats: Stats as SectionComponent,
  credentials: Credentials as SectionComponent,
  contact: Contact as SectionComponent,
  process: Process as SectionComponent,
};

// =============================================================================
// LOOKUP FUNCTION
// =============================================================================

export function getSection(type: SectionType): SectionComponent | null {
  return sectionRegistry[type] ?? null;
}

// =============================================================================
// PLACEHOLDER SECTION
// =============================================================================

interface PlaceholderProps {
  type: SectionType;
}

export function PlaceholderSection({ type }: PlaceholderProps) {
  return (
    <section className="py-20 bg-gray-100">
      <div className="max-w-4xl mx-auto px-4 text-center">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-200 flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
            </svg>
          </div>
          <p className="text-gray-500 font-medium">
            Section: <span className="text-gray-700">{type}</span>
          </p>
          <p className="text-sm text-gray-400 mt-1">
            Component not yet implemented
          </p>
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// EXPORTS
// =============================================================================

export { Hero } from './Hero';
export { Services } from './Services';
export { About } from './About';
export { Testimonials } from './Testimonials';
export { Gallery } from './Gallery';
export { Cta } from './Cta';
export { Faq } from './Faq';
export { Location } from './Location';
export { Stats } from './Stats';
export { Credentials } from './Credentials';
export { Contact } from './Contact';
export { Process } from './Process';
